/**
 * The scenario, which lives in the URL and nowhere else.
 *
 * That is the whole state model: no store, no local storage, no server-side
 * saved scenarios. A link is the unit of sharing, the back button walks
 * through scenarios, and a reload lands exactly where you were.
 *
 * Three properties are deliberate and tested.
 *
 * **Defaults are omitted**, so `serialise(DEFAULT) === ''` and a link stays
 * short enough to paste into a message.
 *
 * **The form is canonical**: fixed key order, lists deduplicated and sorted,
 * so two equal scenarios produce byte-identical URLs. That is what makes a
 * query key usable as a cache key and makes the copied-link test meaningful.
 *
 * **`parse` never throws.** A damaged link degrades to a usable screen plus a
 * warning rather than a blank page -- the most useful property of the lot,
 * since links are the thing people will mangle.
 *
 * Not base64, not compressed: the request was for something readable and
 * shareable, and encoding would satisfy the letter while destroying both.
 */

export type Championship = 'drivers' | 'constructors'
export type DropMode = 'ALL' | 'ALL_BUT_ONE' | 'BEST_N'
export type FastestLapRule = 'NONE' | 'ANY' | 'SHARED' | 'TOP_N'
export type PerSession = 'SUM_POINTS' | 'BEST_POINTS'
export type Eligibility = 'ALL' | 'HAS_POINT' | 'HAS_FINISH' | 'HAS_FINISH_OR_QUALI'
export type TieBreak = 'NONE' | 'COUNTBACK_ALL' | 'COUNTBACK_COUNTED'
export type SharedDrive =
  'NONE' | 'NO_POINTS' | 'SHARED' | 'SHARED_MULTI' | 'SHARED_HIGHEST_FINISH'

export interface DropRule {
  mode: DropMode
  /** Required by `BEST_N` and meaningless otherwise, which the form enforces. */
  n: number | null
}

export interface FastestLap {
  rule: FastestLapRule
  points: string
  maxPosition: number | null
}

/** What each round is worth. An empty map means every round at full points. */
export type RoundValues = Record<number, string>

export interface Scenario {
  yearFrom: number
  yearTo: number
  championship: Championship
  preset: string | null
  /** Points tables to swap in, at most one per session type. */
  schemes: string[]
  drop: DropRule | null
  excludeDrivers: string[]
  excludeConstructors: string[]
  excludeRounds: number[]
  fastestLap: FastestLap | null
  /**
   * null leaves the season's own values alone; {} is "every round at full
   * points", which is the question 1984 asks.
   */
  roundValues: RoundValues | null
  /** Count results the championship penalised or struck out. */
  countPenalised: boolean
  /**
   * Formula 2's rule: a champion is barred from every season after the one he
   * won. The only option here that depends on the other seasons in the range,
   * so it does nothing to a single one.
   */
  barChampions: boolean
  perSession: PerSession | null
  eligibility: Eligibility | null
  tieBreak: TieBreak | null
  countback: number | null
  sharedDrive: SharedDrive | null
  /** How many series the chart asks for. */
  top: number
  /** Competitors pinned into the chart whatever their position. */
  pin: string[]
  /** One row per season instead of one season's table. */
  sweep: boolean
}

export const FIRST_SEASON = 1950
export const LAST_SEASON = 2026
export const DEFAULT_TOP = 10
export const MAX_TOP = 40
/** What the server accepts in `exclude_rounds`; no season comes close. */
export const MAX_ROUNDS = 40

export const DEFAULT: Scenario = {
  yearFrom: 1988,
  yearTo: 1988,
  championship: 'drivers',
  preset: null,
  schemes: [],
  drop: null,
  excludeDrivers: [],
  excludeConstructors: [],
  excludeRounds: [],
  fastestLap: null,
  roundValues: null,
  countPenalised: false,
  barChampions: false,
  perSession: null,
  eligibility: null,
  tieBreak: null,
  countback: null,
  sharedDrive: null,
  top: DEFAULT_TOP,
  pin: [],
  sweep: false,
}

export interface ParseResult {
  scenario: Scenario
  /** One line per token that was ignored, for a banner rather than a crash. */
  warnings: string[]
}

const PER_SESSION: PerSession[] = ['SUM_POINTS', 'BEST_POINTS']
const ELIGIBILITY: Eligibility[] = ['ALL', 'HAS_POINT', 'HAS_FINISH', 'HAS_FINISH_OR_QUALI']
const TIE_BREAKS: TieBreak[] = ['NONE', 'COUNTBACK_ALL', 'COUNTBACK_COUNTED']
const SHARED_DRIVES: SharedDrive[] = [
  'NONE',
  'NO_POINTS',
  'SHARED',
  'SHARED_MULTI',
  'SHARED_HIGHEST_FINISH',
]

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/
const DECIMAL = /^\d+(\.\d+)?$/

// --- parsing -------------------------------------------------------------

/**
 * Read a scenario out of a query string, keeping whatever makes sense.
 *
 * Every rejection is reported rather than raised: one mistyped token must not
 * cost the reader the other nine.
 */
export function parseScenario(input: URLSearchParams | string): ParseResult {
  const params = typeof input === 'string' ? new URLSearchParams(input) : input
  const warnings: string[] = []
  const scenario: Scenario = { ...DEFAULT }

  const warn = (key: string, raw: string, why: string) => {
    warnings.push(`Ignored ${key}=${raw}: ${why}.`)
  }

  const years = params.get('y')
  if (years !== null) {
    const parsed = parseYears(years)
    if (parsed === null) warn('y', years, `expected a season or a range like 1950-2026`)
    else [scenario.yearFrom, scenario.yearTo] = parsed
  }

  const championship = params.get('c')
  if (championship !== null) {
    if (championship === 'c') scenario.championship = 'constructors'
    else if (championship === 'd') scenario.championship = 'drivers'
    else warn('c', championship, 'expected d for drivers or c for constructors')
  }

  const preset = params.get('preset')
  if (preset !== null) {
    if (SLUG.test(preset.replace(/_/g, '-'))) scenario.preset = preset
    else warn('preset', preset, 'not a preset key')
  }

  const scheme = params.get('scheme')
  if (scheme !== null) {
    const keys = splitList(scheme).filter((key) => /^[A-Z0-9_]+$/.test(key))
    if (keys.length === 0) warn('scheme', scheme, 'no points table keys in it')
    else scenario.schemes = canonicalList(keys)
  }

  const drop = params.get('drop')
  if (drop !== null) {
    const parsed = parseDrop(drop)
    if (parsed === null) warn('drop', drop, 'expected all, all-but-one or best:N')
    else scenario.drop = parsed
  }

  for (const [key, field] of [
    ['xd', 'excludeDrivers'],
    ['xc', 'excludeConstructors'],
    ['pin', 'pin'],
  ] as const) {
    const raw = params.get(key)
    if (raw === null) continue
    const slugs = splitList(raw).filter((slug) => SLUG.test(slug) || slug.includes('|'))
    if (slugs.length === 0) warn(key, raw, 'no usable slugs in it')
    else scenario[field] = canonicalList(slugs)
  }

  const rounds = params.get('xr')
  if (rounds !== null) {
    const parsed = parseRounds(rounds)
    if (parsed.length === 0) warn('xr', rounds, 'expected round numbers like 1,5-7')
    else scenario.excludeRounds = parsed.slice(0, MAX_ROUNDS)
  }

  const fastestLap = params.get('fl')
  if (fastestLap !== null) {
    const parsed = parseFastestLap(fastestLap)
    if (parsed === null) warn('fl', fastestLap, 'expected none, any, shared or topN:points')
    else scenario.fastestLap = parsed
  }

  const values = params.get('mult')
  if (values !== null) {
    const parsed = parseRoundValues(values)
    if (parsed === null) warn('mult', values, 'expected full, or pairs like 6:0.5')
    else scenario.roundValues = parsed
  }

  const penalised = params.get('pen')
  if (penalised !== null) {
    if (penalised === '1' || penalised === '0') scenario.countPenalised = penalised === '1'
    else warn('pen', penalised, 'expected 0 or 1')
  }

  const barred = params.get('bar')
  if (barred !== null) {
    if (barred === '1' || barred === '0') scenario.barChampions = barred === '1'
    else warn('bar', barred, 'expected 0 or 1')
  }

  readEnum(params, 'ps', PER_SESSION, warn, (value) => (scenario.perSession = value))
  readEnum(params, 'el', ELIGIBILITY, warn, (value) => (scenario.eligibility = value))
  readEnum(params, 'tb', TIE_BREAKS, warn, (value) => (scenario.tieBreak = value))
  readEnum(params, 'sd', SHARED_DRIVES, warn, (value) => (scenario.sharedDrive = value))

  const countback = params.get('cb')
  if (countback !== null) {
    const depth = toInt(countback)
    if (depth === null || depth < 1 || depth > 50) warn('cb', countback, 'expected 1 to 50')
    else scenario.countback = depth
  }

  const top = params.get('top')
  if (top !== null) {
    const count = toInt(top)
    if (count === null || count < 1 || count > MAX_TOP)
      warn('top', top, `expected 1 to ${MAX_TOP}`)
    else scenario.top = count
  }

  const sweep = params.get('sweep')
  if (sweep !== null) {
    if (sweep === '1' || sweep === '0') scenario.sweep = sweep === '1'
    else warn('sweep', sweep, 'expected 0 or 1')
  }

  return { scenario: coherent(scenario, warnings), warnings }
}

/**
 * Repair combinations the API would reject, so a bad link still renders.
 *
 * `BEST_N` without an `n` is the one that matters: the server used to accept
 * it and silently count every round, which is a wrong answer rather than an
 * error. The `drop=best:11` spelling makes it unrepresentable in a URL, and
 * this is the belt to that braces.
 */
function coherent(scenario: Scenario, warnings: string[]): Scenario {
  if (scenario.drop?.mode === 'BEST_N' && scenario.drop.n === null) {
    warnings.push('Ignored drop=best: best-N needs a number of rounds.')
    scenario.drop = null
  }
  if (scenario.drop !== null && scenario.drop.mode !== 'BEST_N') scenario.drop.n = null
  if (scenario.countback !== null && scenario.tieBreak === null) {
    warnings.push('Ignored cb: a countback depth needs a tie-break rule.')
    scenario.countback = null
  }
  if (scenario.yearFrom > scenario.yearTo) {
    ;[scenario.yearFrom, scenario.yearTo] = [scenario.yearTo, scenario.yearFrom]
  }
  if (scenario.yearFrom !== scenario.yearTo) scenario.sweep = true
  return scenario
}

// --- serialising ---------------------------------------------------------

/**
 * Write a scenario back as a query string, omitting everything at its default.
 *
 * The key order is fixed by this array, not by object iteration, so the output
 * is stable regardless of how the scenario was built.
 */
export function serialiseScenario(scenario: Scenario): string {
  const params = new URLSearchParams()
  const set = (key: string, value: string | number | null) => {
    if (value !== null && value !== '') params.set(key, String(value))
  }

  if (scenario.yearFrom !== DEFAULT.yearFrom || scenario.yearTo !== DEFAULT.yearTo) {
    set(
      'y',
      scenario.yearFrom === scenario.yearTo
        ? scenario.yearFrom
        : `${scenario.yearFrom}-${scenario.yearTo}`,
    )
  }
  if (scenario.championship !== DEFAULT.championship) set('c', 'c')
  set('preset', scenario.preset)
  if (scenario.schemes.length > 0) set('scheme', canonicalList(scenario.schemes).join(','))
  if (scenario.drop !== null) set('drop', writeDrop(scenario.drop))
  if (scenario.excludeDrivers.length > 0)
    set('xd', canonicalList(scenario.excludeDrivers).join(','))
  if (scenario.excludeConstructors.length > 0) {
    set('xc', canonicalList(scenario.excludeConstructors).join(','))
  }
  if (scenario.excludeRounds.length > 0) set('xr', writeRounds(scenario.excludeRounds))
  if (scenario.fastestLap !== null) set('fl', writeFastestLap(scenario.fastestLap))
  if (scenario.roundValues !== null) set('mult', writeRoundValues(scenario.roundValues))
  if (scenario.countPenalised) set('pen', '1')
  if (scenario.barChampions) set('bar', '1')
  set('ps', scenario.perSession)
  set('el', scenario.eligibility)
  set('tb', scenario.tieBreak)
  set('cb', scenario.countback)
  set('sd', scenario.sharedDrive)
  if (scenario.top !== DEFAULT.top) set('top', scenario.top)
  if (scenario.pin.length > 0) set('pin', canonicalList(scenario.pin).join(','))
  // A range already implies a sweep, so the flag would be noise.
  if (scenario.sweep && scenario.yearFrom === scenario.yearTo) set('sweep', '1')

  return params.toString()
}

/** True when nothing has been changed from the defaults. */
export function isPristine(scenario: Scenario): boolean {
  return serialiseScenario(scenario) === ''
}

/** Whether this scenario asks for anything other than what happened. */
export function isCounterfactual(scenario: Scenario): boolean {
  return (
    scenario.preset !== null ||
    scenario.schemes.length > 0 ||
    scenario.drop !== null ||
    scenario.excludeDrivers.length > 0 ||
    scenario.excludeConstructors.length > 0 ||
    scenario.excludeRounds.length > 0 ||
    scenario.fastestLap !== null ||
    scenario.roundValues !== null ||
    scenario.countPenalised ||
    scenario.barChampions ||
    scenario.perSession !== null ||
    scenario.eligibility !== null ||
    scenario.tieBreak !== null ||
    scenario.sharedDrive !== null
  )
}

/** The years this scenario covers, bounded so a typo cannot ask for thousands. */
export function yearsOf(scenario: Scenario): number[] {
  const from = Math.max(FIRST_SEASON, Math.min(scenario.yearFrom, scenario.yearTo))
  const to = Math.min(LAST_SEASON, Math.max(scenario.yearFrom, scenario.yearTo))
  const out: number[] = []
  for (let year = from; year <= to; year += 1) out.push(year)
  return out
}

// --- pieces --------------------------------------------------------------

function parseYears(raw: string): [number, number] | null {
  const match = /^(\d{4})(?:-(\d{4}))?$/.exec(raw.trim())
  if (!match) return null
  const from = Number(match[1])
  const to = match[2] === undefined ? from : Number(match[2])
  if (!inSeasons(from) || !inSeasons(to)) return null
  return [Math.min(from, to), Math.max(from, to)]
}

function inSeasons(year: number): boolean {
  return year >= FIRST_SEASON && year <= LAST_SEASON
}

function parseDrop(raw: string): DropRule | null {
  const value = raw.trim().toLowerCase()
  if (value === 'all') return { mode: 'ALL', n: null }
  if (value === 'all-but-one') return { mode: 'ALL_BUT_ONE', n: null }
  const best = /^best:(\d+)$/.exec(value)
  if (best) {
    const n = toInt(best[1] ?? '')
    return n !== null && n >= 1 ? { mode: 'BEST_N', n } : null
  }
  return null
}

function writeDrop(drop: DropRule): string {
  if (drop.mode === 'ALL') return 'all'
  if (drop.mode === 'ALL_BUT_ONE') return 'all-but-one'
  return `best:${drop.n ?? ''}`
}

/** `1,5-7` becomes `[1, 5, 6, 7]`: ranges keep a long exclusion list short. */
function parseRounds(raw: string): number[] {
  const out = new Set<number>()
  for (const token of splitList(raw)) {
    const range = /^(\d+)-(\d+)$/.exec(token)
    if (range) {
      const from = toInt(range[1] ?? '')
      const to = toInt(range[2] ?? '')
      if (from === null || to === null || from < 1 || to < from || to - from > 50) continue
      for (let round = from; round <= to; round += 1) out.add(round)
      continue
    }
    const single = toInt(token)
    if (single !== null && single >= 1) out.add(single)
  }
  return [...out].sort((a, b) => a - b)
}

/** The inverse: consecutive rounds collapse back into ranges. */
function writeRounds(rounds: number[]): string {
  const sorted = [...new Set(rounds)].sort((a, b) => a - b)
  const parts: string[] = []
  let index = 0
  while (index < sorted.length) {
    let end = index
    while (end + 1 < sorted.length && (sorted[end + 1] ?? 0) === (sorted[end] ?? 0) + 1)
      end += 1
    const from = sorted[index]
    const to = sorted[end]
    parts.push(end - index >= 2 ? `${from}-${to}` : sorted.slice(index, end + 1).join(','))
    index = end + 1
  }
  return parts.join(',')
}

/**
 * `full` or `6:0.5,19:2`.
 *
 * `full` is spelled out rather than being the empty string, because "every
 * round at full points" is an instruction and the empty string is silence.
 */
function parseRoundValues(raw: string): RoundValues | null {
  const value = raw.trim().toLowerCase()
  if (value === 'full') return {}

  const out: RoundValues = {}
  for (const token of splitList(raw)) {
    const match = /^(\d+):(\d+(?:\.\d+)?)$/.exec(token)
    if (!match) return null
    const round = toInt(match[1] ?? '')
    if (round === null || round < 1) return null
    out[round] = match[2] ?? '1'
  }
  return Object.keys(out).length > 0 ? out : null
}

export function writeRoundValues(values: RoundValues): string {
  const rounds = Object.keys(values)
    .map(Number)
    .sort((a, b) => a - b)
  if (rounds.length === 0) return 'full'
  return rounds.map((round) => `${round}:${values[round]}`).join(',')
}

function parseFastestLap(raw: string): FastestLap | null {
  const [head = '', tail] = raw.trim().split(':')
  const value = head.toLowerCase()
  const points = tail !== undefined && DECIMAL.test(tail) ? tail : '1'

  if (value === 'none') return { rule: 'NONE', points: '0', maxPosition: null }
  if (value === 'any') return { rule: 'ANY', points, maxPosition: null }
  if (value === 'shared') return { rule: 'SHARED', points, maxPosition: null }
  const top = /^top(\d+)$/.exec(value)
  if (top) {
    const position = toInt(top[1] ?? '')
    if (position === null || position < 1) return null
    return { rule: 'TOP_N', points, maxPosition: position }
  }
  return null
}

function writeFastestLap(rule: FastestLap): string {
  if (rule.rule === 'NONE') return 'none'
  if (rule.rule === 'TOP_N') return `top${rule.maxPosition ?? 10}:${rule.points}`
  return `${rule.rule.toLowerCase()}:${rule.points}`
}

function readEnum<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: T[],
  warn: (key: string, raw: string, why: string) => void,
  assign: (value: T) => void,
): void {
  const raw = params.get(key)
  if (raw === null) return
  const value = raw.toUpperCase() as T
  if (allowed.includes(value)) assign(value)
  else warn(key, raw, `expected one of ${allowed.join(', ').toLowerCase()}`)
}

function splitList(raw: string): string[] {
  return raw
    .split(',')
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '')
}

/** Deduplicated and sorted, so two equal scenarios serialise identically. */
function canonicalList(values: string[]): string[] {
  return [...new Set(values)].sort()
}

function toInt(raw: string): number | null {
  return /^\d+$/.test(raw.trim()) ? Number(raw.trim()) : null
}
